export const translations = {
  meta: {
    title: 'Aprende Angular testing con Jest y Testing Library',
    description:
      'Una historia real sobre cómo el testing puede cambiar tu carrera. Aprende Angular testing con Jest y Testing Library de forma práctica y moderna. Guía completa para desarrolladores.',
  },
  sections: {
    bookCover: {
      title: 'Testing en Angular',
      description:
        'Aprende a implementar pruebas unitarias efectivas en Angular con esta guía práctica y completa. Diseñada para desarrolladores que buscan mejorar la calidad de su código y adoptar las mejores prácticas de testing.',
      features: [
        'Guía paso a paso desde cero hasta avanzado',
        'Ejemplos prácticos y casos de uso reales',
        'Cobertura completa de Jest y Testing Library',
        'Mejores prácticas y patrones de testing',
      ],
    },
    whatIsTesting: {
      title: '¿Qué son las Pruebas Unitarias?',
      content:
        'Las pruebas unitarias son un método de testing de software donde se prueban unidades o componentes individuales de forma aislada. En Angular, esto significa probar componentes individuales, servicios y otras características para asegurar que funcionan como se espera. Las pruebas unitarias ayudan a detectar errores temprano en el proceso de desarrollo y proporcionan confianza al realizar cambios en el código.',
    },
    whyJestAndTestingLibrary: {
      title: '¿Por qué Jest y Testing Library?',
      content:
        'Jest es un potente framework de testing que proporciona una solución completa para probar aplicaciones JavaScript. Cuando se combina con Testing Library, ofrece un enfoque centrado en el usuario para probar componentes Angular. Esta combinación te permite escribir pruebas que se centran en cómo los usuarios interactúan con tu aplicación, haciendo que tus pruebas sean más mantenibles y menos frágiles.',
    },
    keyFeatures: {
      title: 'Características Principales',
      items: [
        {
          title: 'Configuración Fácil',
          description: 'Configuración rápida y sencilla de Jest y Testing Library en tu proyecto Angular',
        },
        {
          title: 'Testing de Componentes',
          description: 'Aprende a probar componentes de Angular de forma eficiente y efectiva',
        },
        {
          title: 'Testing de Servicios',
          description: 'Domina las técnicas para probar servicios y dependencias en Angular',
        },
        {
          title: 'Mejores Prácticas',
          description: 'Sigue las mejores prácticas de la industria para escribir pruebas mantenibles y efectivas',
        },
      ],
    },
    dondeComprar: {
      title: '¿Dónde comprar el libro?',
      description:
        'Ahora puedes adquirir "Testing en Angular" directamente en Amazon o Leanpub. Elige tu plataforma preferida para comprarlo en formato Kindle, impreso o PDF.',
      button: 'Comprar en Amazon',
      buttonLeanpub: 'Comprar en Leanpub',
    },
    cursoUdemy: {
      title: '¿Sabías que Angular 20 ha cambiado por completo la forma de desarrollar aplicaciones web?',
      description: `Olvídate de métodos anticuados, código lento o soluciones que ya no funcionan. En mi curso de Angular Moderno, aprenderás las técnicas más actuales y eficientes que te convertirán en un desarrollador Angular a prueba de futuro.`,
      features: [
        '✅ Aprende con Angular 20: Signals, Componentes Standalone, @if, @for, y mucho más.',
        '✅ Proyectos prácticos reales: No te pierdas en teoría, aprende haciendo.',
        '✅ Actualizaciones constantes: Acceso de por vida a futuras novedades del framework.',
        '🔴 ¡Oferta limitada! El curso subirá de precio pronto. Asegura tu plaza ahora y ahorra dinero.',
      ],
      button: 'Ir al curso en Udemy',
    },
  },
  navigation: {
    home: 'Inicio',
    reality: 'La Realidad',
    features: 'Características',
    testimonials: 'Testimonios',
    chapters: 'Capítulos',
    blog: 'Blog',
    videos: 'Videos',
  },
  all: {
    ctaButton: 'Comprar e-book Ahora',
  },
  hero: {
    title: '¿Te imaginas perder el trabajo de tus sueños por no saber escribir un test?',
    subtitle: 'A mí me pasó.',
    description:
      'Fue en una entrevista técnica, de esas que te ponen a prueba con un live coding en tiempo real. Todo iba bien… hasta que llegó el momento de escribir los test unitarios. Me bloqueé. No era falta de experiencia, ni desconocimiento del negocio. Simplemente, nunca me lo había tomado en serio.',
    story: 'El testing era esa tarea que siempre dejaba para "después". Y ese "después" me costó caro.',
    promise:
      'Salí de esa entrevista con una promesa. Que nunca más iba a subestimar el testing. Que iba a aprenderlo a fondo. Pero sobre todo, que todo lo que aprendiera lo iba a compartir. Porque nadie merece perder una oportunidad así por no estar preparado para algo tan esencial.',
    secondaryButton: 'Ver contenido',
  },
  reality: {
    title: 'El testing no frena. Acelera.',
    description:
      'Durante años, el testing unitario en el frontend ha sido una tarea postergada. En muchas organizaciones, incluso con equipos experimentados, se ha asumido que testear componentes, servicios o formularios era opcional. Las excusas han sido siempre las mismas: falta de tiempo, entregas urgentes, presión por lanzar nuevas funcionalidades o simplemente la creencia de que "probar manualmente es suficiente".',
    consequences:
      'Pero esta mentalidad tiene consecuencias: regresiones en producción, errores que se descubren tarde, componentes frágiles ante cualquier refactor, y una deuda técnica que crece sin hacer ruido… hasta que explota.',
    reality:
      'En un mundo donde el frontend ha dejado de ser "la capa bonita" y se ha convertido en una pieza crítica del sistema, no tener tests automatizados es, literalmente, jugar con fuego.',
    benefits: [
      {
        title: 'Confianza para refactorizar',
        description: 'Cuando está bien hecho, da confianza para refactorizar sin miedo',
      },
      {
        title: 'Nuevas features sin temor',
        description: 'Añade nuevas funcionalidades sabiendo que no romperás lo existente',
      },
      {
        title: 'Código más seguro',
        description: 'Asegura que lo que funcionaba ayer seguirá funcionando mañana',
      },
      {
        title: 'Herramientas modernas',
        description: 'Angular ha cambiado: Signals, componentes standalone, Testing Library, Jest',
      },
    ],
  },
  features: {
    title: 'Lo que aprenderás',
    description:
      'Este e-book te proporcionará conocimientos prácticos y aplicables inmediatamente en tus proyectos Angular',
    items: [
      {
        title: 'Configuración de Jest en Angular',
        description: 'Aprende a integrar Jest en tu proyecto Angular desde cero',
      },
      {
        title: 'Testing Library y sus ventajas',
        description: 'Descubre por qué Testing Library es la mejor opción para tus pruebas de componentes',
      },
      {
        title: 'Pruebas de Signals en Angular',
        description: 'Domina el testing de la nueva API de Signals en Angular',
      },
      {
        title: 'Mocking de servicios',
        description: 'Técnicas efectivas para crear mocks de servicios y dependencias',
      },
      {
        title: 'Testing de Pipes',
        description: 'Aprende a probar pipes personalizados y pipes integrados de Angular',
      },
      {
        title: 'Test Driven Development',
        description: 'Implementa TDD en tu flujo de trabajo diario',
      },
    ],
  },
  testimonials: {
    title: 'Lo que dicen los desarrolladores',
    items: [
      {
        name: 'María González',
        role: 'Senior Frontend Developer',
        company: 'Tech Solutions',
        comment:
          'Esta guía transformó mi enfoque al testing en Angular. Ahora mis aplicaciones son mucho más robustas.',
      },
      {
        name: 'Carlos Rodríguez',
        role: 'Tech Lead',
        company: 'Innovative Apps',
        comment:
          'El capítulo sobre Signals en Angular es especialmente valioso. Información difícil de encontrar en otros recursos.',
      },
      {
        name: 'Laura Pérez',
        role: 'Angular Developer',
        company: 'Digital Services',
        comment: 'Por fin entiendo cómo implementar TDD correctamente en mis proyectos Angular. Muy recomendado.',
      },
    ],
  },
  videos: {
    title: 'Videos complementarios',
    description: 'Refuerza tu aprendizaje con estos videos tutoriales del canal Dominicode',
  },
  content: {
    title: 'No se trata solo de cubrir una asignatura pendiente',
    description:
      'Se trata de escribir código más seguro, más mantenible y más profesional. Este libro nace como respuesta a esa realidad. No como un manifiesto teórico, sino como una guía práctica, moderna, y con los pies en la tierra. Pensado para quienes construyen aplicaciones reales con Angular.',
    purpose:
      'Porque sí: hacer tests en el frontend es posible, rentable y, sobre todo, una forma concreta de ganar tiempo, reducir errores y mejorar la calidad del software.',
    formTitle: 'Comprar el e-book',
    nameLabel: 'Nombre',
    emailLabel: 'Correo electrónico',
    submitButton: 'Lo quiero',
    privacyNotice: 'No compartiremos tu información con terceros.',
    features: [
      '✅ Guía práctica y moderna para aplicaciones reales',
      '✅ Testing con Signals y componentes standalone',
      '✅ Herramientas actuales: Jest y Testing Library',
      '✅ Código más seguro, mantenible y profesional',
    ],
  },
  footer: {
    copyright: '© 2025 DOMINICODE, LLC. Todos los derechos reservados.',
    links: {
      privacy: 'Política de privacidad',
      terms: 'Términos y condiciones',
      contact: 'Contacto',
    },
    description:
      'Recursos avanzados para el testing en Angular con Jest y Testing Library. Mejora la calidad de tu código con pruebas unitarias eficientes.',
    resources: {
      title: 'Recursos',
      testingLibrary: 'Testing Library Docs',
      jest: 'Jest Docs',
    },
  },
  faq: {
    title: 'Preguntas Frecuentes',
    audience: {
      question: '¿Para quién es este ebook?',
      answer:
        'Este ebook está dirigido a desarrolladores Angular de nivel intermedio que desean aprender a testear. También es ideal para desarrolladores sénior con experiencia que buscan perfeccionar sus habilidades en Unit Testing con Jest y Testing Library.',
    },
    prerequisites: {
      question: '¿Qué prerequisitos necesito para entender el ebook?',
      answer:
        'Necesitas tener conocimientos básicos de JavaScript, TypeScript y el framework Angular. No es necesario tener experiencia previa en testing; ¡el ebook te guiará desde cero!',
    },
    tools: {
      question: '¿Qué herramientas se cubren en el ebook?',
      answer:
        'El ebook se centra específicamente en Jest como runner de tests y Testing Library (concretamente @testing-library/angular) para escribir tests de una manera que fomente buenas prácticas y resultados más robustos.',
    },
    integration: {
      question: '¿Este ebook cubre testing de integración o end-to-end?',
      answer:
        'No, este ebook se enfoca exclusivamente en el Unit Testing. Se centra en cómo testear componentes, servicios y otras unidades de código de forma aislada. Además, aprenderás a enfocar tus tests en cómo interactúan tus usuarios con la aplicación.',
    },
    learn: {
      question: '¿Qué aprenderé exactamente con este ebook?',
      items: {
        setup: 'Configurar tu entorno de testing con Jest y Testing Library en un proyecto Angular.',
        components: 'Escribir tests unitarios para componentes Angular, servicios, pipes y directivas.',
        interactions: 'Simular interacciones de usuario de forma efectiva con Testing Library.',
        dependencies: 'Manejar dependencias y mocks de forma limpia.',
        maintainable: 'Escribir tests que sean mantenibles y resistentes a refactorizaciones.',
        bestPractices: 'Aplicar buenas prácticas de testing para mejorar la calidad de tu código.',
      },
    },
    examples: {
      question: '¿El ebook incluye ejemplos de código o proyectos de ejemplo?',
      answer:
        'Sí, el ebook está lleno de ejemplos de código prácticos. La idea es que puedas seguir los ejemplos y aplicarlos directamente en tus propios proyectos.',
    },
    updates: {
      question: '¿Recibiré actualizaciones si las herramientas evolucionan?',
      answer:
        'Sí, el ebook se actualizará periódicamente para reflejar los cambios y las mejores prácticas en Angular, Jest y Testing Library. Tendrás acceso a estas actualizaciones sin costo adicional.',
    },
    format: {
      question: '¿Cuál es el formato del ebook?',
      answer:
        'El ebook estará disponible en formato PDF y también en Amazon Kindle para que puedas leerlo en tu dispositivo favorito.',
    },
    quality: {
      question: '¿Este ebook me ayudará a mejorar la calidad de mi código Angular?',
      answer:
        'Definitivamente. Al aprender a escribir tests unitarios efectivos, no solo asegurarás que tu código funciona como esperas, sino que también mejorarás su diseño, modularidad y mantenibilidad.',
    },
  },
  chapters: {
    sectionTitle: 'Contenido del Ebook',
    chapter1: {
      title: 'Capítulo I',
      subtitle: 'Fundamentos del testing en Angular moderno',
      description: 'Aprende los conceptos básicos y fundamentales para comenzar con el testing en Angular.',
    },
    chapter2: {
      title: 'Capítulo II',
      subtitle: 'Pruebas unitarias con Signals y componentes standalone',
      description: 'Domina las pruebas unitarias utilizando las últimas características de Angular.',
    },
    chapter3: {
      title: 'Capítulo III',
      subtitle: 'Servicios, lógica compleja y mocks',
      description: 'Profundiza en el testing de servicios y la implementación de mocks efectivos.',
    },
    chapter4: {
      title: 'Capítulo IV',
      subtitle: 'Formularios, directivas y otros elementos comunes',
      description: 'Aprende a probar formularios, directivas y otros componentes frecuentes en Angular.',
    },
    chapter5: {
      title: 'Capítulo V',
      subtitle: 'Buenas prácticas y casos comunes',
      description: 'Descubre las mejores prácticas y cómo manejar casos de uso comunes en el testing.',
    },
    chapter6: {
      title: 'Capítulo VI',
      subtitle: 'Alternativa moderna: Testing en Angular con Vitest',
      description: 'Descubre Vitest, una alternativa moderna y veloz a Jest, perfecta para proyectos Angular con Vite.',
    },
  },
  emailSubscription: {
    title: '¿Te interesa el eBook?',
    description: 'El libro ya está disponible y el curso está en el horno.\n\n Serás el primero en enterarte y recibirás contenido exclusivo.',
    placeholder: 'Tu email',
    button: 'Quiero el aviso',
    loading: 'Enviando...',
    successMessage: '¡Gracias por suscribirte!',
    errorMessage: 'Hubo un error al guardar tu email. Por favor, intenta de nuevo.',
    duplicateMessage: '¡Ya estás en la lista! Te avisaremos cuando el ebook esté disponible.',
  },
  privacy: {
    title: 'Política de Privacidad',
    lastUpdate: 'Última actualización: 15 de junio de 2025',
    responsibleTitle: '1. Responsable del tratamiento',
    responsible: 'DOMINICODE, LLC, e-mail contacto: privacy@dominicode.com.',
    dataTitle: '2. Datos que recopilamos',
    data: 'Información de contacto (nombre, e-mail) cuando compras o te suscribes.\n\nDatos de navegación mediante cookies y tecnologías similares (por ej., Meta Pixel) que registran visitas, clics y compras.\n\nPreferencias de marketing y métricas de apertura de correos.',
    purposesTitle: '3. Finalidades y bases legales',
    purposesTable: [
      {
        purpose: 'Procesar tu compra y enviar el eBook',
        legal: 'Ejecución de contrato (art. 6 b RGPD)',
        retention: '5 años o lo que exija la ley fiscal',
      },
      {
        purpose: 'Enviar newsletter y promociones',
        legal: 'Consentimiento expreso (art. 6 a)',
        retention: 'Hasta que retires tu consentimiento',
      },
      {
        purpose: 'Analítica y publicidad dirigida con Meta Pixel',
        legal: 'Consentimiento expreso (art. 6 a)',
        retention: '26 meses',
      },
    ],
    cookiesTitle: '4. Cookies y tecnologías de seguimiento',
    cookies:
      'Este sitio usa Meta Pixel de Meta Platforms Inc. (1601 Willow Rd, Menlo Park, CA 94025, EE. UU.) para medir conversiones y mostrar anuncios relevantes en Facebook e Instagram.\nAl aceptar las cookies de marketing:\n\nSe envía tu IP, agente de usuario y eventos (p.ej. PageView, Purchase), que pueden transferirse a EE. UU. bajo las cláusulas contractuales tipo de la UE.\n\nPuedes revocar tu consentimiento en cualquier momento desde el banner de cookies o escribiendo a privacy@dominicode.com.\n\nMás información en la Política de datos de Meta.\nes-la.facebook.com',
    recipientsTitle: '5. Destinatarios',
    recipients:
      'Meta Platforms Inc. (Publicidad y analítica)\n\nProveedor de email marketing mailerlite\n\nPasarela de pago Stripe (tokenizada; no almacenamos datos de tarjeta)\n\nTodos nuestros proveedores han suscrito contratos de encargo de tratamiento conforme al art. 28 RGPD.',
    rightsTitle: '6. Derechos de las personas usuarias',
    rights:
      'Puedes ejercer acceso, rectificación, supresión, oposición, limitación o portabilidad enviando un correo a privacy@dominicode.com con copia de tu DNI o documento equivalente. Si no quedas satisfecho/a, tienes derecho a reclamar ante la AEPD (www.aepd.es).',
    securityTitle: '7. Seguridad',
    security: 'Aplicamos cifrado TLS, control de acceso y auditorías periódicas para proteger tu información.',
    changesTitle: '8. Cambios',
    changes:
      'Nos reservamos el derecho de modificar esta Política para adaptarla a novedades legislativas o de funcionamiento. Publicaremos cualquier cambio con al menos 10 días de antelación.',
    entity: 'DOMINICODE, LLC (Delaware LLC, EIN: 32-0804117)',
  },
};
